import { Link } from 'react-router-dom';
import type {
  AssessmentActivityResponse,
  AssessmentCoverageEntry,
  AssessmentCoverageResponse,
  DirectoryResponse,
} from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { formatTimestamp, sourceLabel } from '../components/profile/attributeDisplay';
import styles from './DashboardPage.module.css';

/**
 * Dashboard — "what do I need to know" and "what needs my attention"
 * (docs/UX_ARCHITECTURE.md §4).
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS ALLOWED TO SAY
 *
 * Everything here is a count of something the domain computed, shown next
 * to the denominator it was counted against, in the directory's own order.
 * There is no score, no percentage, no "health", and no sorting by who
 * looks worst — `docs/MVP_SCOPE.md` §6 places cross-employee ranking
 * outside the MVP, and a list ordered by sparsest record is that ranking
 * with the number left off.
 *
 * The distinction the page works hardest to keep is between an absence and
 * a finding. "2 of 23 assessed" means twenty-one attributes nobody has ever
 * assessed. It does not mean the person scored low on twenty-one things,
 * and the wording says so rather than trusting the reader to infer it.
 *
 * WHAT §4.1 ASKS FOR AND WHY ONE ITEM IS MISSING
 *
 * §4.1's attention list names "overdue assessments". Nothing in this
 * domain defines when an assessment is due: there is no cadence, no
 * schedule, no next-review date in the schema. So nothing can be overdue,
 * and inventing a period to make the widget look complete would put a
 * policy nobody agreed to in front of a manager as though the system knew
 * it. What can be said truthfully is *stale* — older than the confidence
 * policy's threshold, which is a real, versioned rule — and that is what
 * appears.
 *
 * THREE REQUESTS, NOT ONE
 *
 * The three sources load independently, so a failure in one leaves the
 * other two readable. A dashboard that goes entirely blank because its
 * activity query was slow is worse than one that says which part is
 * missing.
 * ---------------------------------------------------------------------
 */
export function DashboardPage() {
  const coverage = useApiResource<AssessmentCoverageResponse>('/assessment-coverage');
  const activity = useApiResource<AssessmentActivityResponse>('/assessment-activity');
  const roster = useApiResource<DirectoryResponse>('/employees');

  const scope =
    coverage.status === 'loaded' ? coverage.data.scope : roster.status === 'loaded' ? roster.data.scope : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Dashboard</h1>
        <p className={styles.scope}>
          {scope === 'organisation'
            ? 'Everyone in your organisation'
            : scope === 'responsibility'
              ? 'The people you are responsible for'
              : scope === 'self_and_direct_reports'
                ? 'You and the people you lead'
                : 'Loading…'}
          {coverage.status === 'loaded' ? ` · as at ${formatTimestamp(coverage.data.asOf)}` : ''}
        </p>
      </header>

      {/* Two independent columns rather than one grid of four equal cells:
          "Recent assessment activity" can run to twenty rows while the other
          three stay short, and a same-height grid row would leave the short
          cells stranded above a wall of empty space the height of the tall
          one. Splitting the tall widget into its own column lets the short
          ones stack tightly beside it instead. */}
      <div className={styles.grid}>
        <div className={styles.mainColumn}>
          <AttentionWidget state={coverage} />
          <ActivityWidget state={activity} />
        </div>
        <div className={styles.sideColumn}>
          <CoverageWidget state={coverage} />
          <RosterWidget state={roster} />
        </div>
      </div>
    </div>
  );
}

/* --- Widgets --------------------------------------------------------- */

type Resource<T> = ReturnType<typeof useApiResource<T>>;

function Widget({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className={styles.widget} aria-label={title}>
      <h2 className={styles.widgetTitle}>{title}</h2>
      {note ? <p className={styles.widgetNote}>{note}</p> : null}
      {children}
    </section>
  );
}

function CoverageWidget({ state }: { state: Resource<AssessmentCoverageResponse> }) {
  return (
    <Widget
      title="Assessment coverage"
      note="How much of the attribute library has ever been assessed for each person. An attribute with no assessments is a gap in the record, not a low rating."
    >
      {state.status === 'loading' ? <LoadingState label="Loading coverage…" /> : null}
      {state.status === 'failed' ? (
        <ErrorState title="Coverage could not be loaded" detail={describeError(state.error)} />
      ) : null}
      {state.status === 'loaded' ? (
        state.data.employees.length === 0 ? (
          <EmptyState title="No people to show" detail="Nobody is currently visible to you here." />
        ) : (
          <ul className={styles.list}>
            {state.data.employees.map((employee) => (
              <li key={employee.employeeId} className={styles.row}>
                <Link to={`/people/${employee.employeeId}`} className={styles.person}>
                  {employee.fullName}
                </Link>
                <span className={styles.counts}>
                  <strong>
                    {employee.assessedCount} of {employee.attributeCount}
                  </strong>{' '}
                  attributes assessed
                </span>
                {employee.assessedCount === 0 ? (
                  <span className={styles.absent}>No assessments recorded yet</span>
                ) : null}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Widget>
  );
}

/** Whether a coverage row has anything to raise. */
function attentionItems(employee: AssessmentCoverageEntry): string[] {
  const items: string[] = [];
  if (employee.staleCount > 0) {
    items.push(
      `${employee.staleCount} ${employee.staleCount === 1 ? 'attribute has' : 'attributes have'} not been reassessed recently`
    );
  }
  if (employee.disagreementCount > 0) {
    items.push(
      `${employee.disagreementCount} ${employee.disagreementCount === 1 ? 'attribute where sources' : 'attributes where sources'} disagree`
    );
  }
  return items;
}

function AttentionWidget({ state }: { state: Resource<AssessmentCoverageResponse> }) {
  return (
    <Widget
      title="Needs attention"
      note="Assessments that have aged past the staleness threshold, and attributes where different sources disagree. Disagreement is a prompt for a conversation, not an error to resolve."
    >
      {state.status === 'loading' ? <LoadingState label="Loading attention items…" /> : null}
      {state.status === 'failed' ? (
        <ErrorState title="Attention items could not be loaded" detail={describeError(state.error)} />
      ) : null}
      {state.status === 'loaded'
        ? (() => {
            const flagged = state.data.employees
              .map((employee) => ({ employee, items: attentionItems(employee) }))
              .filter((entry) => entry.items.length > 0);

            if (flagged.length === 0) {
              return (
                <EmptyState
                  title="Nothing is flagged"
                  detail="No assessment has aged past the staleness threshold and no attribute has sources disagreeing. This is not the same as everyone being fully assessed — see assessment coverage."
                />
              );
            }

            return (
              <ul className={styles.list}>
                {flagged.map(({ employee, items }) => (
                  <li key={employee.employeeId} className={styles.row}>
                    <Link to={`/people/${employee.employeeId}`} className={styles.person}>
                      {employee.fullName}
                    </Link>
                    <ul className={styles.items}>
                      {items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            );
          })()
        : null}
    </Widget>
  );
}

function ActivityWidget({ state }: { state: Resource<AssessmentActivityResponse> }) {
  return (
    <Widget
      title="Recent assessment activity"
      note="The most recently dated assessments for the people you can see. Dates are when an assessment was made, not when it was entered — a backdated assessment appears at its own date. The assessor is not identified; only the perspective they assessed from."
    >
      {state.status === 'loading' ? <LoadingState label="Loading activity…" /> : null}
      {state.status === 'failed' ? (
        <ErrorState title="Activity could not be loaded" detail={describeError(state.error)} />
      ) : null}
      {state.status === 'loaded' ? (
        state.data.entries.length === 0 ? (
          <EmptyState
            title="No assessments recorded"
            detail="Nobody you can see has a recorded attribute assessment yet."
          />
        ) : (
          <>
            <ul className={styles.list}>
              {state.data.entries.map((entry) => (
                <li key={entry.assessmentId} className={styles.activityRow}>
                  <Link to={`/people/${entry.employeeId}`} className={styles.person}>
                    {entry.fullName}
                  </Link>
                  <span className={styles.activityDetail}>
                    {entry.attribute.name} · {entry.rating} of 20 · {sourceLabel(entry.sourceType)}
                    {entry.effective ? null : (
                      <span className={styles.superseded}>
                        {' '}
                        · {entry.recordStatus === 'withdrawn' ? 'withdrawn' : 'since corrected'}
                      </span>
                    )}
                  </span>
                  <span className={styles.when}>{formatTimestamp(entry.assessedAt)}</span>
                </li>
              ))}
            </ul>
            {state.data.hasMore ? (
              <p className={styles.more}>
                Showing the {state.data.limit} most recent. Older assessments are on each person’s profile.
              </p>
            ) : null}
          </>
        )
      ) : null}
    </Widget>
  );
}

function RosterWidget({ state }: { state: Resource<DirectoryResponse> }) {
  return (
    <Widget title="Team" note="Who you can see, and where they sit.">
      {state.status === 'loading' ? <LoadingState label="Loading team…" /> : null}
      {state.status === 'failed' ? (
        <ErrorState title="This team could not be loaded" detail={describeError(state.error)} />
      ) : null}
      {state.status === 'loaded' ? (
        state.data.employees.length === 0 ? (
          <EmptyState title="No people to show" detail="Nobody is currently visible to you here." />
        ) : (
          <ul className={styles.list}>
            {state.data.employees.map((employee) => (
              <li key={employee.id} className={styles.row}>
                <Link to={`/people/${employee.id}`} className={styles.person}>
                  {employee.fullName}
                </Link>
                <span className={styles.counts}>
                  {employee.currentRoleName ?? <span className={styles.absent}>No current role</span>}
                  {employee.currentTeamName ? ` · ${employee.currentTeamName}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Widget>
  );
}
